import { TrackingPageSettings } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import type { TrackingPageShipment } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo } from "react";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { getHeaderStyles } from "../shared/block-styles";

enum CustomCompanyPickUp {
  ANDAR = "andar-wallets.myshopify.com",
  IVY_CITY = "ivy-city-co.myshopify.com",
}

interface PickupInfo {
  hours: string;
  address: string;
  condition: boolean;
}

interface CustomPickupProps {
  storeUrl: string;
  shipment?: TrackingPageShipment;
  isPickUpOrder: boolean;
  pageSettings: TrackingPageSettings | undefined;
}

/**
 * Custom pickup block for specific companies
 */
export const CustomPickupBlock = memo(function CustomPickupBlock({
  storeUrl,
  shipment,
  isPickUpOrder,
  pageSettings,
}: CustomPickupProps) {
  const pickupInfo = getPickupInfo(storeUrl, shipment, isPickUpOrder);

  if (!pickupInfo) {
    return null;
  }

  const headerStyles = getHeaderStyles(pageSettings);

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="md" fontWeight="semibold" style={headerStyles}>
        In store pick up hours:
      </Text>
      <Text
        fontSize="md"
        style={{
          fontFamily: pageSettings?.typography.bodyFontFamily,
          color: pageSettings?.typography.bodyFontColor,
        }}
      >
        {pickupInfo.hours}
      </Text>
      <Text fontSize="md" fontWeight="semibold" style={headerStyles}>
        Store Address:
      </Text>
      <Text
        fontSize="md"
        style={{
          fontFamily: pageSettings?.typography.bodyFontFamily,
          color: pageSettings?.typography.bodyFontColor,
        }}
      >
        {pickupInfo.address}
      </Text>
    </Flex>
  );
});

function getPickupInfo(
  storeUrl: string,
  shipment?: TrackingPageShipment,
  isPickUpOrder = false,
): PickupInfo | null {
  if (
    !Object.values(CustomCompanyPickUp).includes(
      storeUrl as CustomCompanyPickUp,
    )
  ) {
    return null;
  }

  const pickupConfigs: Record<CustomCompanyPickUp, PickupInfo> = {
    [CustomCompanyPickUp.ANDAR]: {
      hours: "Monday - Friday 9am to 5pm",
      address: "4320 E Warner Rd. Suite 101 Gilbert, AZ 85296",
      condition: isAndarShipmentInStorePickup(shipment),
    },
    [CustomCompanyPickUp.IVY_CITY]: {
      hours: "Monday - Saturday 10am to 7pm",
      address: "12711 Old Liberty Way Suite 700 Riverton, Utah 84096",
      condition: isPickUpOrder,
    },
  };

  const config = pickupConfigs[storeUrl as CustomCompanyPickUp];
  return config?.condition ? config : null;
}

export function isAndarShipmentInStorePickup(
  shipment?: TrackingPageShipment,
): boolean {
  return !!(shipment && shipment.carrier === "Self-Pickup (by PDQ)");
}
